import { Inject, Injectable, InjectionToken } from '@angular/core';
import { type DeviceRepository } from '@telespot/domain';
import { filter, map, skip, switchMap, take, tap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { type IListDevicesHandler, ListDevices, ListedDevice } from '@telespot/management/use-cases';
import { PaginatedStateEffects } from '@shared/ui';
import { DeviceData, DeviceState, DeviceStoreState } from './device.state';

export const DEVICE_REPOSITORY = new InjectionToken<DeviceRepository>('DEVICE_REPOSITORY');
export const LIST_DEVICES = new InjectionToken<IListDevicesHandler>('LIST_DEVICES');

@Injectable()
export class DeviceEffects extends PaginatedStateEffects<DeviceData, DeviceStoreState> {
  constructor(
    protected override readonly paginatedState: DeviceState,
    // TODO: repositories should not be injected here, should be a use case
    @Inject(DEVICE_REPOSITORY) private readonly deviceRepository: DeviceRepository,
    @Inject(LIST_DEVICES) private readonly listDevicesHandler: IListDevicesHandler
  ) {
    super(paginatedState);
    this.initializeDevices(this.paginatedState.organizationId$.pipe(skip(1)));
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public get organizationId$() {
    return this.paginatedState.organizationId$;
  }
  public get devices$() {
    return this.paginatedState.devices$;
  }
  public get totalDeviceCount$() {
    return this.paginatedState.totalDeviceCount$;
  }
  public get nextDeviceToken$() {
    return this.paginatedState.nextDeviceToken$;
  }
  public get previousDeviceTokens$() {
    return this.paginatedState.previousDeviceTokens$;
  }
  public get devicesLoaded$() {
    return this.paginatedState.devicesLoaded$;
  }
  public get noDevices$() {
    return this.paginatedState.noDevices$;
  }

  // ============================================================================
  // Updater aliases
  // ============================================================================

  public readonly setOrganizationId = (organizationId: string | undefined) =>
    this.paginatedState.setOrganizationId(organizationId);
  public readonly setSearchTerm = (searchTerm: string) => this.paginatedState.setSearchTerm(searchTerm);
  public readonly changePageSize = (pageSize: number) => this.paginatedState.changePageSize(pageSize);

  // ============================================================================
  // Backward compatibility aliases
  // ============================================================================

  public readonly countDevices = this.countItems;
  public readonly loadMoreDevices = this.loadMoreItems;

  // ============================================================================
  // Custom effects
  // ============================================================================

  public readonly initializeDevices = this.effect((action$: Observable<string | undefined>) =>
    action$.pipe(tap(() => this.initializeItems()))
  );

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected override async countRepository(searchTerm?: string): Promise<number> {
    const organizationId = await this.paginatedState.organizationId$.pipe(take(1)).toPromise();
    // TODO: repositories should not be used here, should be a use case
    return this.deviceRepository.count(organizationId, searchTerm);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected override listFlow(
    limit: number,
    lastItem: DeviceData | undefined,
    searchTerm: string | undefined
  ): Observable<DeviceData[]> {
    return this.paginatedState.organizationId$.pipe(
      take(1),
      switchMap((organizationId) =>
        from(
          this.listDevicesHandler.execute(
            new ListDevices({ organizationId, searchTerm, limit, olderThan: lastItem?.createdAt })
          )
        ).pipe(
          filter((devices): devices is ListedDevice[] => devices !== null),
          map((devices: ListedDevice[]) =>
            devices.map((device) => ({
              id: device.id,
              name: device.name,
              organizationName: device.organizationName,
              organizationLogoUrl: device.organizationLogoUrl,
              deviceTypeId: device.deviceType?.id,
              deviceTypeName: device.deviceType?.name ?? '',
              deviceTypeVersion: device.deviceType?.version ?? '',
              serialNumber: device.serialNumber,
              createdAt: device.createdAt,
            }))
          )
        )
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override processDeferredAttributes(_itemId: string): Observable<Partial<DeviceData>> {
    return of();
  }
}
